import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styles from "./AdminShared.module.css";
import { toTitleCase } from "./adminUtils";

/** Keys shown as summary chips / not dumped again in the full field list */
const SERVICE_KEYS = new Set([
  "availableServices",
  "inactiveServices",
  "availableServicesNamesObj",
]);

/** Prefer these catalog fields near the top of the list */
const CATALOG_FIELD_PRIORITY = [
  "type",
  "tags",
  "artist",
  "slug",
  "active",
  "title",
];

/** Prefer these content fields near the top */
const CONTENT_FIELD_PRIORITY = [
  "type",
  "slug",
  "active",
  "title",
  "addToNavMenu",
];

/**
 * Normalize metadata value shapes:
 * - [count, ...values]
 * - [count, arrayOfValues] (tags)
 * - plain array / string / object
 */
const parseMetaEntry = (value) => {
  if (value == null) {
    return { count: 0, values: [], kind: "empty" };
  }

  if (Array.isArray(value)) {
    const rest = value.slice(1);
    const chips = [];
    rest.forEach((entry) => {
      if (Array.isArray(entry)) chips.push(...entry);
      else if (entry != null && entry !== "") chips.push(entry);
    });
    const unique = [
      ...new Set(
        chips
          .map((c) => String(c).trim())
          .filter((c) => c && c.toLowerCase() !== "undefined"),
      ),
    ].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));

    const count =
      typeof value[0] === "number" && Number.isFinite(value[0])
        ? value[0]
        : unique.length;

    return {
      count,
      values: unique,
      kind: "list",
    };
  }

  if (typeof value === "object") {
    return {
      count: Object.keys(value).length,
      values: value,
      kind: "object",
    };
  }

  const s = String(value).trim();
  if (!s) return { count: 0, values: [], kind: "empty" };
  return { count: 1, values: [s], kind: "list" };
};

const sortFieldKeys = (keys, priority = []) => {
  const p = priority.map((k) => k.toLowerCase());
  return [...keys].sort((a, b) => {
    const ia = p.indexOf(String(a).toLowerCase());
    const ib = p.indexOf(String(b).toLowerCase());
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return String(a).localeCompare(String(b));
  });
};

const PREVIEW_LIMIT = 8;

const ChipList = ({ values, expanded, onToggle }) => {
  if (!values.length) {
    return <span className={styles["row-muted"]}>No values</span>;
  }

  const shown = expanded ? values : values.slice(0, PREVIEW_LIMIT);
  const hidden = values.length - shown.length;

  return (
    <div className={styles["meta-chips"]}>
      {shown.map((c, i) => (
        <span
          key={`${c}-${i}`}
          className={styles["meta-chip"]}
          title={String(c)}
        >
          {String(c)}
        </span>
      ))}
      {hidden > 0 && !expanded && (
        <button
          type="button"
          className={styles["meta-chip-more"]}
          onClick={onToggle}
        >
          +{hidden} more
        </button>
      )}
      {expanded && values.length > PREVIEW_LIMIT && (
        <button
          type="button"
          className={styles["meta-chip-more"]}
          onClick={onToggle}
        >
          Show less
        </button>
      )}
    </div>
  );
};

const MetaFieldRow = ({ fieldKey, value }) => {
  const [open, setOpen] = useState(false);
  const [showAllValues, setShowAllValues] = useState(false);
  const parsed = useMemo(() => parseMetaEntry(value), [value]);

  const countLabel =
    parsed.kind === "object"
      ? `${parsed.count} key${parsed.count === 1 ? "" : "s"}`
      : `${parsed.count} value${parsed.count === 1 ? "" : "s"}`;

  return (
    <div
      className={`${styles["meta-row"]} ${
        open ? styles["meta-row-open"] : ""
      }`.trim()}
    >
      <button
        type="button"
        className={styles["meta-row-toggle"]}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={styles["meta-row-toggle-main"]}>
          <span className={styles["meta-chevron"]} aria-hidden="true">
            {open ? "▾" : "▸"}
          </span>
          <span className={styles["meta-key"]}>{toTitleCase(fieldKey)}</span>
        </span>
        <span className={styles["meta-count"]}>{countLabel}</span>
      </button>

      {open && (
        <div className={styles["meta-row-body"]}>
          {parsed.kind === "empty" && (
            <span className={styles["row-muted"]}>—</span>
          )}
          {parsed.kind === "list" && (
            <ChipList
              values={parsed.values}
              expanded={showAllValues}
              onToggle={() => setShowAllValues((e) => !e)}
            />
          )}
          {parsed.kind === "object" && (
            <pre className={styles["meta-pre"]}>
              {JSON.stringify(parsed.values, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
};

const MetaSection = ({
  title,
  subtitle,
  metadata,
  priority = [],
  excludeKeys = SERVICE_KEYS,
  defaultOpen = false,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const fieldEntries = useMemo(() => {
    if (!metadata || typeof metadata !== "object") return [];
    const keys = Object.keys(metadata).filter((k) => !excludeKeys.has(k));
    return sortFieldKeys(keys, priority).map((key) => [
      key,
      metadata[key],
      parseMetaEntry(metadata[key]),
    ]);
  }, [metadata, priority, excludeKeys]);

  const totalValues = useMemo(
    () => fieldEntries.reduce((sum, [, , p]) => sum + (p.count || 0), 0),
    [fieldEntries],
  );

  return (
    <section className={styles["meta-section"]}>
      <button
        type="button"
        className={styles["meta-section-toggle"]}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={styles["meta-section-title-wrap"]}>
          <span className={styles["meta-section-title"]}>{title}</span>
          {subtitle ? (
            <span className={styles["meta-section-sub"]}>{subtitle}</span>
          ) : null}
        </span>
        <span className={styles["meta-section-meta"]}>
          <span className={styles["meta-count"]}>
            {fieldEntries.length} field{fieldEntries.length === 1 ? "" : "s"}
            {totalValues > 0 ? ` · ${totalValues} values` : ""}
          </span>
          <span className={styles["meta-chevron"]} aria-hidden="true">
            {open ? "▾" : "▸"}
          </span>
        </span>
      </button>

      {open && (
        <div className={styles["meta-section-body"]}>
          {fieldEntries.length === 0 ? (
            <p className={styles["row-muted"]}>No field metadata available.</p>
          ) : (
            fieldEntries.map(([key, value]) => (
              <MetaFieldRow key={key} fieldKey={key} value={value} />
            ))
          )}
        </div>
      )}
    </section>
  );
};

const ServiceSnapshot = ({ catalogMetadata }) => {
  const available = useMemo(
    () =>
      (catalogMetadata?.availableServices || [])
        .filter(Boolean)
        .map(String)
        .sort((a, b) => a.localeCompare(b)),
    [catalogMetadata],
  );
  const inactive = useMemo(
    () =>
      (catalogMetadata?.inactiveServices || [])
        .filter(Boolean)
        .map(String)
        .sort((a, b) => a.localeCompare(b)),
    [catalogMetadata],
  );
  const namesObj = catalogMetadata?.availableServicesNamesObj;

  return (
    <div className={styles.panel}>
      <h2 className={styles["panel-title"]}>Services</h2>
      <p className={styles["field-hint"]} style={{ marginTop: "-0.5rem" }}>
        Streaming platforms used by catalog items (active vs inactive).
      </p>

      <div className={styles["meta-summary-grid"]}>
        <div className={styles["meta-summary-card"]}>
          <p className={styles["stat-label"]}>Available</p>
          <p className={styles["stat-value"]}>{available.length}</p>
        </div>
        <div className={styles["meta-summary-card"]}>
          <p className={styles["stat-label"]}>Inactive</p>
          <p className={styles["stat-value"]}>{inactive.length}</p>
        </div>
        <div className={styles["meta-row"]}>
          <div className={styles["meta-row-head"]}>
            <span className={styles["meta-key"]}>Active services</span>
          </div>
          <div className={styles["meta-chips"]}>
            {available.length ? (
              available.map((s) => (
                <span key={s} className={styles["meta-chip-service"]}>
                  {s}
                </span>
              ))
            ) : (
              <span className={styles["row-muted"]}>None</span>
            )}
          </div>
        </div>
      </div>

      {inactive.length > 0 && (
        <div className={styles["meta-row"]}>
          <div className={styles["meta-row-head"]}>
            <span className={styles["meta-key"]}>Inactive services</span>
          </div>
          <div className={styles["meta-chips"]}>
            {inactive.map((s) => (
              <span key={s} className={styles["meta-chip"]}>
                {s}
              </span>
            ))}
          </div>
        </div>
      )}

      {namesObj &&
        typeof namesObj === "object" &&
        Object.keys(namesObj).length > 0 && (
          <details className={styles["meta-details"]}>
            <summary>Service display names map</summary>
            <pre className={styles["meta-pre"]}>
              {JSON.stringify(namesObj, null, 2)}
            </pre>
          </details>
        )}
    </div>
  );
};

const AdminMetadata = () => {
  const catalogMetadata = useSelector(
    (state) => state.catalogData.catalogMetadata,
  );
  const contentMetadata = useSelector(
    (state) => state.contentData.contentMetadata,
  );
  const catalog = useSelector((state) => state.catalogData.catalog);
  const content = useSelector((state) => state.contentData.content);

  const catalogCount =
    catalog && typeof catalog === "object" ? Object.keys(catalog).length : 0;
  const contentCount =
    content && typeof content === "object" ? Object.keys(content).length : 0;

  const catalogTypes = useMemo(() => {
    const parsed = parseMetaEntry(catalogMetadata?.type);
    return parsed.kind === "list" ? parsed.values : [];
  }, [catalogMetadata]);

  const contentTypes = useMemo(() => {
    const parsed = parseMetaEntry(contentMetadata?.type);
    return parsed.kind === "list" ? parsed.values : [];
  }, [contentMetadata]);

  const tagCount = useMemo(() => {
    const parsed = parseMetaEntry(catalogMetadata?.tags);
    return parsed.count || 0;
  }, [catalogMetadata]);

  return (
    <div>
      <div className={styles["page-header"]}>
        <div className={styles["page-header-content"]}>
          <h1 className={styles["page-title"]}>Metadata</h1>
          <p className={styles["page-subtitle"]}>
            Live field inventory — services, types, tags, and more. Expand a
            section to inspect every value.
          </p>
        </div>
      </div>

      {/* At-a-glance */}
      <div className={styles["meta-summary-grid"]}>
        <div className={styles["meta-summary-card"]}>
          <p className={styles["stat-label"]}>Catalog items</p>
          <p className={styles["stat-value"]}>{catalogCount}</p>
          <p className={styles["stat-meta"]}>
            {catalogTypes.length
              ? `${catalogTypes.length} type${catalogTypes.length === 1 ? "" : "s"}`
              : "No type metadata"}
          </p>
        </div>
        <div className={styles["meta-summary-card"]}>
          <p className={styles["stat-label"]}>Content items</p>
          <p className={styles["stat-value"]}>{contentCount}</p>
          <p className={styles["stat-meta"]}>
            {contentTypes.length
              ? `${contentTypes.length} type${contentTypes.length === 1 ? "" : "s"}`
              : "No type metadata"}
          </p>
        </div>
        <div className={styles["meta-summary-card"]}>
          <p className={styles["stat-label"]}>Unique tags</p>
          <p className={styles["stat-value"]}>{tagCount}</p>
          <p className={styles["stat-meta"]}>From catalog items</p>
        </div>
        <div className={styles["meta-summary-card"]}>
          <p className={styles["stat-label"]}>Services</p>
          <p className={styles["stat-value"]}>
            {(catalogMetadata?.availableServices || []).filter(Boolean).length}
          </p>
          <p className={styles["stat-meta"]}>
            {(catalogMetadata?.inactiveServices || []).filter(Boolean).length}{" "}
            inactive
          </p>
        </div>
      </div>

      <ServiceSnapshot catalogMetadata={catalogMetadata} />

      <div className={styles["meta-sections"]}>
        <MetaSection
          title="Catalog fields"
          subtitle={
            catalogTypes.length
              ? `Types: ${catalogTypes.map((t) => toTitleCase(t)).join(", ")}`
              : "Distinct values across catalog schema fields"
          }
          metadata={catalogMetadata}
          priority={CATALOG_FIELD_PRIORITY}
          defaultOpen
        />
        <MetaSection
          title="Content fields"
          subtitle={
            contentTypes.length
              ? `Types: ${contentTypes.map((t) => toTitleCase(t)).join(", ")}`
              : "Distinct values across content schema fields"
          }
          metadata={contentMetadata}
          priority={CONTENT_FIELD_PRIORITY}
          defaultOpen={false}
        />
      </div>
    </div>
  );
};

export default AdminMetadata;
